import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Mail, ShoppingCart, ArrowRight, Check } from "lucide-react";
import { fetchProduct, postEnquiry } from "@/lib/api";
import { PageHero, Section } from "@/components/Section";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import ProductCard from "@/components/ProductCard";

const cleanCat = (t) => (t || "").split("Manufacturer")[0].split("Hand Tool")[0].trim() || t;

const ProductDetail = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("description");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setP(null); setSent(false); setTab("description");
    fetchProduct(slug).then(setP).catch((e) => setErr(e?.response?.status || "error"));
    window.scrollTo({ top: 0 });
  }, [slug]);

  const onEnquire = async (e) => {
    e.preventDefault();
    try {
      await postEnquiry({ ...form, subject: `Enquiry: ${p?.title}`, product_slug: slug });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setErr("send failed");
    }
  };

  if (err === 404) {
    return (
      <Section>
        <h2 className="font-display text-3xl">Product not found</h2>
        <Link to="/products" className="mt-4 inline-flex jt-btn jt-btn-outline">All Products</Link>
      </Section>
    );
  }
  if (!p) return <Section><div className="font-mono text-sm text-[var(--jt-text-muted)]">Loading...</div></Section>;

  const markets = p.marketplaces || {};

  return (
    <>
      <PageHero
        eyebrow={cleanCat(p.category_title) || "Product"}
        title={p.title}
        breadcrumbs={
          <>Home / <Link className="hover:text-white" to="/products">Products</Link> / <Link className="hover:text-white" to={`/category/${p.category_slug}`}>{cleanCat(p.category_title)}</Link> / <span className="text-white truncate">{p.title}</span></>
        }
      />
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] border border-[var(--jt-border)] bg-white flex items-center justify-center overflow-hidden" data-testid="product-hero-img">
              {p.photo_file ? (
                <img
                  src={`/products/photos/${p.photo_file}`}
                  alt={p.header_image_alt || p.title}
                  className="w-full h-full object-contain p-8"
                  loading="lazy"
                />
              ) : (
                <ProductPlaceholder title={p.title} subtitle={`JT-${String(p.legacy_id || 0).padStart(4, "0")}`} className="w-full h-full" />
              )}
            </div>
            {p.spec_file && (
              <a
                href={`/products/specs/${p.spec_file}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 font-mono text-[0.65rem] tracking-widest uppercase text-[var(--jt-text-muted)] hover:text-[var(--jt-primary)]"
                data-testid="product-spec-sheet-link"
              >
                View Master Spec Sheet <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* Buy box */}
          <div className="lg:col-span-5">
            <div className="font-mono text-xs text-[var(--jt-text-muted)] tracking-widest uppercase">SKU&nbsp;·&nbsp;JT-{String(p.legacy_id || 0).padStart(4, "0")}</div>
            <h1 className="mt-2 font-display font-black uppercase tracking-tight text-3xl lg:text-4xl leading-tight" data-testid="product-title">{p.title}</h1>
            <Link to={`/category/${p.category_slug}`} className="mt-2 inline-block eyebrow">{cleanCat(p.category_title)}</Link>

            {p.is_featured && (
              <div className="mt-3 inline-flex items-center gap-2 bg-[var(--jt-primary)]/10 border border-[var(--jt-primary)] text-[var(--jt-primary)] font-mono text-xs uppercase tracking-widest px-3 py-1">
                <Check className="w-3 h-3" /> Featured
              </div>
            )}

            <div className="mt-6 jt-prose text-sm" dangerouslySetInnerHTML={{ __html: p.features || "" }} />

            {/* Marketplaces */}
            <div className="mt-6">
              <div className="eyebrow">Buy On</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {["amazon", "flipkart", "snapdeal", "indiamart"].map((m) =>
                  markets[m] ? (
                    <a key={m} href={markets[m]} target="_blank" rel="noopener noreferrer" data-testid={`buy-${m}`} className="border border-[var(--jt-border)] hover:border-[var(--jt-primary)] py-3 flex items-center justify-center gap-2 font-display font-bold uppercase text-xs tracking-wide transition-colors">
                      <ShoppingCart className="w-3 h-3" /> {m}
                    </a>
                  ) : null
                )}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-2">
              <a href="#enquire" className="jt-btn jt-btn-primary" data-testid="enquire-cta">
                <Mail className="w-4 h-4" /> Enquire
              </a>
              <button onClick={() => nav(`/category/${p.category_slug}`)} className="jt-btn jt-btn-outline" data-testid="back-to-cat">
                See Family <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16 border-t border-[var(--jt-border)]">
          <div className="flex flex-wrap gap-0 -mb-px" data-testid="product-tabs">
            {[
              { k: "description", l: "Description" },
              { k: "specification", l: "Specification" },
              { k: "include", l: "Includes" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                className={`px-6 py-4 font-display font-bold uppercase text-sm tracking-wide border-b-2 transition-colors ${
                  tab === t.k ? "border-[var(--jt-primary)] text-[var(--jt-primary)]" : "border-transparent text-[var(--jt-text-muted)] hover:text-[var(--jt-ink)]"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
          <div className="mt-8 max-w-4xl jt-prose jt-spec">
            {tab === "specification" && p.spec_file ? (
              <div data-testid="spec-sheet-image">
                <img
                  src={`/products/specs/${p.spec_file}`}
                  alt={`${p.title} specification sheet`}
                  className="w-full h-auto border border-[var(--jt-border)] bg-white"
                  loading="lazy"
                />
                <div className="mt-3 font-mono text-[0.65rem] tracking-widest uppercase text-[var(--jt-text-muted)]">
                  Master Catalogue · Spec Sheet
                </div>
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: p[tab] || "<p>No content provided for this section.</p>" }} />
            )}
          </div>
        </div>

        {/* Enquire form */}
        <div id="enquire" className="mt-16 bg-[var(--jt-ink)] text-white p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="eyebrow">Bulk · OEM · Dealer</div>
              <h2 className="mt-2 font-display font-black uppercase text-3xl lg:text-4xl tracking-tight leading-tight">Need pricing or a custom quote for this product?</h2>
              <p className="mt-3 text-white/70">Drop us a message — our team usually replies within 1 business day.</p>
            </div>
            <form onSubmit={onEnquire} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-enquiry-form">
              <input required placeholder="Your name" data-testid="enq-name" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input required type="email" placeholder="Email" data-testid="enq-email" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input required placeholder="Phone" data-testid="enq-phone" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)] sm:col-span-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea required rows={4} placeholder="Your message (qty, country, deadline, etc.)" data-testid="enq-msg" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)] sm:col-span-2" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button type="submit" data-testid="enq-submit" className="jt-btn jt-btn-primary sm:col-span-2">{sent ? "Sent ✓" : "Send Enquiry"} <ArrowRight className="w-4 h-4" /></button>
              {sent && <div className="font-mono text-xs text-[var(--jt-primary)] sm:col-span-2" data-testid="enq-success">Enquiry received. We'll be in touch within 24 hours.</div>}
            </form>
          </div>
        </div>

        {/* Related */}
        {p.related?.length ? (
          <div className="mt-16">
            <h3 className="font-display font-black uppercase text-2xl mb-6">Related in {cleanCat(p.category_title)}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {p.related.slice(0, 4).map((r) => <ProductCard key={r.slug} p={r} />)}
            </div>
          </div>
        ) : null}
      </Section>
    </>
  );
};

export default ProductDetail;
